import React, { useMemo, useState } from 'react'

const buildOptions = (options) =>
  Array.isArray(options)
    ? options
    : Object.entries(options || {}).map(([value, label]) => ({ value, label }))

const EventFiltersBar = ({
  searchTerm = '',
  onSearchChange,
  sortOption = '',
  onSortChange,
  sortOptions = [],
  leagueOption = '',
  onLeagueChange,
  leagueOptions = [],
  timeWindow = '',
  onTimeWindowChange,
  timeOptions = [],
  advancedFilters = {},
  onAdvancedFilterChange,
  badges = [],
  onReset,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false)

  const normalisedSortOptions = useMemo(() => buildOptions(sortOptions), [sortOptions])
  const normalisedLeagueOptions = useMemo(() => buildOptions(leagueOptions), [leagueOptions])
  const normalisedTimeOptions = useMemo(() => buildOptions(timeOptions), [timeOptions])

  const handleAdvancedChange = (key, value) => {
    if (typeof onAdvancedFilterChange === 'function') {
      onAdvancedFilterChange({
        ...advancedFilters,
        [key]: value,
      })
    }
  }

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-customAccent/20 bg-white/90 p-4 shadow-lg shadow-customAccent/10 backdrop-blur-sm dark:border-customSurfaceMuted dark:bg-customSurface/90 dark:text-customHeading">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => onSearchChange?.(event.target.value)}
              placeholder="Search teams, leagues, handicaps..."
              className="w-60 rounded-xl border border-customMuted/40 bg-white/80 px-4 py-2 text-sm text-customPrimaryText shadow-inner outline-none transition focus:border-customAccent focus:ring-2 focus:ring-customAccent/30 dark:border-customSurfaceMuted dark:bg-customSurface dark:text-customHeading"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-customSecondaryText dark:text-customMuted">
              🔍
            </span>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-customSecondaryText dark:text-customMuted">
              Sort
            </label>
            <select
              value={sortOption}
              onChange={(event) => onSortChange?.(event.target.value)}
              className="rounded-xl border border-customMuted/40 bg-white px-3 py-2 text-sm text-customPrimaryText shadow-sm outline-none transition hover:border-customAccent/50 focus:border-customAccent focus:ring-2 focus:ring-customAccent/30 dark:border-customSurfaceMuted dark:bg-customSurface dark:text-customHeading"
            >
              {normalisedSortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {/* <div className="flex items-center gap-2">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-customSecondaryText dark:text-customMuted">
              League
            </label>
            <select
              value={leagueOption}
              onChange={(event) => onLeagueChange?.(event.target.value)}
              className="rounded-xl border border-customMuted/40 bg-white px-3 py-2 text-sm text-customPrimaryText shadow-sm outline-none transition hover:border-customAccent/50 focus:border-customAccent focus:ring-2 focus:ring-customAccent/30 dark:border-customSurfaceMuted dark:bg-customSurface dark:text-customHeading"
            >
              <option value="">All</option>
              {normalisedLeagueOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div> */}
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-customSecondaryText dark:text-customMuted">
              Time
            </label>
            <select
              value={timeWindow}
              onChange={(event) => onTimeWindowChange?.(event.target.value)}
              className="rounded-xl border border-customMuted/40 bg-white px-3 py-2 text-sm text-customPrimaryText shadow-sm outline-none transition hover:border-customAccent/50 focus:border-customAccent focus:ring-2 focus:ring-customAccent/30 dark:border-customSurfaceMuted dark:bg-customSurface dark:text-customHeading"
            >
              {normalisedTimeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {badges.map((badge) => (
            <span
              key={badge}
              className="rounded-full border border-customAccent/30 bg-customAccent/10 px-3 py-1 text-xs font-semibold text-customAccent dark:border-customAccent/50 dark:bg-customAccent/20 dark:text-customHeading"
            >
              {badge}
            </span>
          ))}
          <button
            type="button"
            onClick={() => setShowAdvanced((value) => !value)}
            className="flex items-center gap-2 rounded-xl border border-customAccentBlue/40 px-4 py-2 text-sm font-semibold text-customAccentBlue transition hover:bg-customAccentBlue/10 focus:outline-none focus:ring-2 focus:ring-customAccentBlue/30 dark:border-customAccent/50 dark:text-customHeading dark:hover:bg-customAccent/10"
          >
            {showAdvanced ? 'Hide advanced' : 'Advanced filters'}
          </button>
          {onReset ? (
            <button
              type="button"
              onClick={onReset}
              className="rounded-xl border border-transparent bg-customAccentBlue px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-customAccentBlue/30 transition hover:bg-customAccentBlue/80 focus:outline-none focus:ring-2 focus:ring-customAccentBlue/30 dark:bg-customAccent dark:text-customHeading"
            >
              Reset
            </button>
          ) : null}
        </div>
      </div>
      {showAdvanced ? (
        <div className="grid gap-4 rounded-2xl border border-dashed border-customAccent/20 bg-customSlate/60 p-4 text-xs text-customSecondaryText dark:border-customSurfaceMuted dark:bg-customSurfaceMuted/60 dark:text-customMuted">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-customInk dark:text-customHeading">
            Suggested advanced filters
          </h3>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-2">
              Handicap trend
              <select
                value={advancedFilters.handicapTrend ?? 'all'}
                onChange={(event) => handleAdvancedChange('handicapTrend', event.target.value)}
                className="rounded-xl border border-customMuted/40 bg-white px-3 py-2 text-sm text-customPrimaryText shadow-sm outline-none transition hover:border-customAccent/50 focus:border-customAccent focus:ring-2 focus:ring-customAccent/30 dark:border-customSurfaceMuted dark:bg-customSurface dark:text-customHeading"
              >
                <option value="all">All handicaps</option>
                <option value="rising">Rising handicap</option>
                <option value="dropping">Dropping handicap</option>
                <option value="stable">Unchanged</option>
              </select>
            </label>
            <label className="flex flex-col gap-2">
              Odds delta (±)
              <input
                type="number"
                min="0"
                step="0.01"
                value={advancedFilters.oddsDelta ?? ''}
                onChange={(event) => handleAdvancedChange('oddsDelta', event.target.value)}
                placeholder="e.g. 0.25"
                className="rounded-xl border border-customMuted/40 bg-white px-3 py-2 text-sm text-customPrimaryText shadow-inner outline-none transition focus:border-customAccent focus:ring-2 focus:ring-customAccent/30 dark:border-customSurfaceMuted dark:bg-customSurface dark:text-customHeading"
              />
            </label>
            <label className="flex flex-col gap-2">
              Goal line focus
              <select
                value={advancedFilters.goalLine ?? 'any'}
                onChange={(event) => handleAdvancedChange('goalLine', event.target.value)}
                className="rounded-xl border border-customMuted/40 bg-white px-3 py-2 text-sm text-customPrimaryText shadow-sm outline-none transition hover:border-customAccent/50 focus:border-customAccent focus:ring-2 focus:ring-customAccent/30 dark:border-customSurfaceMuted dark:bg-customSurface dark:text-customHeading"
              >
                <option value="any">Any</option>
                <option value="high">High (3.0+)</option>
                <option value="balanced">Balanced (2.0 - 2.75)</option>
                <option value="low">Low (≤ 1.75)</option>
              </select>
            </label>
            <label className="flex flex-col gap-2">
              Updated within
              <select
                value={advancedFilters.updatedWithin ?? 'any'}
                onChange={(event) => handleAdvancedChange('updatedWithin', event.target.value)}
                className="rounded-xl border border-customMuted/40 bg-white px-3 py-2 text-sm text-customPrimaryText shadow-sm outline-none transition hover:border-customAccent/50 focus:border-customAccent focus:ring-2 focus:ring-customAccent/30 dark:border-customSurfaceMuted dark:bg-customSurface dark:text-customHeading"
              >
                <option value="any">Any time</option>
                <option value="5m">Last 5 minutes</option>
                <option value="15m">Last 15 minutes</option>
                <option value="1h">Last hour</option>
                <option value="24h">Last 24 hours</option>
              </select>
            </label>
          </div>
          <p className="text-[11px] text-customSecondaryText dark:text-customMuted">
            Advanced filters help you surface volatile markets faster. Combine odds delta with updated
            within to catch rapid line movement before it goes live.
          </p>
        </div>
      ) : null}
    </section>
  )
}

export default EventFiltersBar

